import { API } from '@vp/common';
import { defineStore } from 'pinia';
import { ApolloClient, ApolloLink, Observable } from '@apollo/client/core/index';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import type { FetchResult, NextLink, NormalizedCacheObject, Operation } from '@apollo/client/core';
import type { ObservableSubscription } from '@apollo/client/utilities';
import { computed, Ref, ref, watch, type WatchStopHandle } from 'vue';

const AUTH_TOKEN_KEY = 'vpAuthToken';
const INTERNAL_API_APP = 'internal';

// How long an operation waits for an account that is on its way. Only a caller that never resolves
// one at all reaches this -- the app assigns `null` the moment it knows there is no account, which
// fails the operation immediately. It exists so that a caller which forgets to resolve either way
// gets a reported error rather than a spinner that never stops.
const ACCOUNT_WAIT_MS = 15_000;

// Passed instead of the store's `requireAccount` by a chain that must never refuse for want of an
// account. It still sends one when there is one, and still honours a per-operation `forAccount`.
const ACCOUNT_OPTIONAL = ref(false);

if (import.meta.env.DEV) {
  loadDevMessages();
  loadErrorMessages();
}

let apiClientInstance: ApolloClient<NormalizedCacheObject> | undefined;
let gqlApiClientInstance: ApolloClient<NormalizedCacheObject> | undefined;
let internalGqlApiClientInstance: ApolloClient<NormalizedCacheObject> | undefined;

const authMiddleware = new ApolloLink((operation, forward) => {
  const connectionStore = useConnectionStore();
  const authToken = connectionStore.authToken;
  if (!authToken) {
    return forward(operation);
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: `Bearer ${authToken}`,
    },
  }));

  return forward(operation);
});

const langMiddleware = (locale: Ref<string | undefined>) => new ApolloLink((operation, forward) => {
  if (!locale.value) {
    return forward(operation);
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-VP-Locale': locale.value,
    },
  }));

  return forward(operation);
});

const appIdMiddleware = (appId: Ref<string | undefined>, apiApp: Ref<string | undefined>) => new ApolloLink((operation, forward) => {
  // if (!appId.value) {
  //   return forward(operation);
  // }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-VP-App-ID': appId?.value,
      'x-vp-api': apiApp?.value,
    },
  }));

  return forward(operation);
});

// The internal (VP staff) schema takes no app id, so it gets its own link rather
// than reusing appIdMiddleware with a pair of constant refs.
const internalApiMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-vp-api': INTERNAL_API_APP,
    },
  }));

  return forward(operation);
});

const withAccountHeader = (operation: Operation, forward: NextLink, accountId: number) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'X-VP-Account-Id': accountId,
    },
  }));

  return forward(operation);
};

const refuse = (operation: Operation) => new Observable<FetchResult>(observer => {
  observer.error(new API.MissingAccountError(operation.operationName));
});

/**
 * Hold the operation until the account is known, then send it -- or fail it if the answer turns out
 * to be that there is none.
 *
 * `undefined` on the ref means "not resolved yet", which is a race rather than a failure: the app
 * is still working out which account this is. Sending anyway is the bug this whole link exists to
 * stop, and failing would break every read issued while the app boots.
 */
const holdUntilResolved = (operation: Operation, forward: NextLink, accountId: Ref<number | null | undefined>) =>
  new Observable<FetchResult>(observer => {
    let inner: ObservableSubscription | undefined;

    const cleanup = () => {
      clearTimeout(timer);
      stop();
    };

    const settle = (resolved: number | null) => {
      cleanup();

      if (resolved === null) {
        observer.error(new API.MissingAccountError(operation.operationName));
        return;
      }

      inner = withAccountHeader(operation, forward, resolved).subscribe(observer);
    };

    const timer = setTimeout(() => settle(null), ACCOUNT_WAIT_MS);

    // Synchronous flush: this is the network layer, and there is no reason for a queued request to
    // wait on a render tick once the account it needs has arrived.
    const stop: WatchStopHandle = watch(accountId, value => {
      if (value !== undefined) {
        settle(value);
      }
    }, { flush: 'sync' });

    // Apollo does not promise to subscribe synchronously, so the account can arrive between the
    // link returning this observable and the subscriber running. The watcher would never fire for
    // that value, and the caller would wait out the backstop for an account that is already known.
    // Has to come after both declarations above: `settle` clears them.
    if (accountId.value !== undefined) {
      settle(accountId.value);
    }

    return () => {
      cleanup();
      inner?.unsubscribe();
    };
  });

/**
 * Puts the account on the request, and refuses to send an account-scoped one without it.
 *
 * A request that arrives with no `X-VP-Account-Id` is not rejected by the backend -- both GraphQL
 * controllers fall back to `current_user.default_account`, which is `accounts.order(:id).first`,
 * an arbitrary tenant. So it succeeds, against the wrong account: an event the operator is looking
 * straight at reads back "not found", and an attachment is filed under someone else's client.
 *
 * Two escapes, both declared per operation in `@vp/common`'s `accountContext`: `ACCOUNT_AGNOSTIC`
 * for the operations that genuinely have no account (the pre-login handful -- login, config,
 * discovery, webauthn, the staff probe -- and most of the internal VP-staff surface, which addresses
 * platform registries and globally-unique records), and `forAccount(id)` for a caller that knows the
 * account and cannot get it from this ref -- the VP-staff screens under `/s`, which administer
 * accounts their operator is not a member of. Anything that declares neither is account-scoped, so
 * new work fails closed rather than silently rejoining the bug.
 *
 * `requireAccount` is opt-in per consumer, and only `@vp/admin` opts in. Checkout builds its
 * clients from this same store, has no signed-in user to mis-default to, and resolves its tenancy
 * from the event -- enforcing there would stall and then fail the ticket-buying path for nothing.
 */
const accountMiddleware = (accountId: Ref<number | null | undefined>, requireAccount: Ref<boolean>) => {
  return new ApolloLink((operation, forward) => {
    const { vpAccountAgnostic, vpAccountId } = operation.getContext() as API.AccountContext;

    if (vpAccountAgnostic) {
      return forward(operation);
    }

    if (vpAccountId) {
      return withAccountHeader(operation, forward, vpAccountId);
    }

    if (accountId.value) {
      return withAccountHeader(operation, forward, accountId.value);
    }

    if (!requireAccount.value) {
      return forward(operation);
    }

    if (accountId.value === null) {
      return refuse(operation);
    }

    return holdUntilResolved(operation, forward, accountId);
  });
};

export const useConnectionStore = defineStore('@vp/connection', () => {
  const apiBase = ref<string | undefined>();
  const gqlApiEndpoint = ref<string | undefined>();
  const appId = ref<string | undefined>();
  const apiApp = ref<string | undefined>();

  const gqlApiApp = ref<string | undefined>();
  const gqlApiAppId = ref<string | undefined>();

  const internalApiEndpoint = ref<string | undefined>();

  // Three states, not two. A number is the account; `null` means the app has resolved the question
  // and there is no account; `undefined` means it has not resolved it yet. `accountMiddleware`
  // treats the last two very differently -- see it for why.
  const accountId = ref<number | null | undefined>();
  const requireAccount = ref(false);
  const authToken = ref<string | undefined>(typeof window !== 'undefined' ? localStorage.getItem(AUTH_TOKEN_KEY) || undefined : undefined);
  const localeRef = ref<string | undefined>();
  const prependMiddleware = ref<ApolloLink[] | undefined>();

  const apiClient = computed(
    (): ApolloClient<NormalizedCacheObject> | undefined => {
      if (!apiBase.value) {
        throw new Error('apiBase is not set');
      }

      if (typeof window !== 'undefined') {
        // We're in browser, we can cache the client
        if (!apiClientInstance) {
          apiClientInstance = API.Client.createApiClient({
            apiBase: apiBase.value,
            middleware: [...(prependMiddleware.value || []), accountMiddleware(accountId, requireAccount), appIdMiddleware(appId, apiApp), authMiddleware, langMiddleware(localeRef)],
          });
        }
        return apiClientInstance;
      } else {
        // In SSR, always create a new instance so it doesn't leak between requests
        return API.Client.createApiClient({
          apiBase: apiBase.value,
          middleware: [appIdMiddleware(appId, apiApp), accountMiddleware(accountId, requireAccount), authMiddleware, langMiddleware(localeRef)],
        });
      }
    },
  );

  const gqlApiClient = computed(
    (): ApolloClient<NormalizedCacheObject> | undefined => {
      if (!gqlApiEndpoint.value) {
        throw new Error('gqlApiEndpoint is not set');
      }

      if (typeof window !== 'undefined') {
        // We're in browser, we can cache the client
        if (!gqlApiClientInstance) {
          gqlApiClientInstance = API.Client.createApiClient({
            apiEndpoint: gqlApiEndpoint.value,
            middleware: [...(prependMiddleware.value || []), accountMiddleware(accountId, requireAccount), appIdMiddleware(gqlApiAppId, gqlApiApp), authMiddleware, langMiddleware(localeRef)],
          });
        }
        return gqlApiClientInstance;
      } else {
        // In SSR, always create a new instance so it doesn't leak between requests
        return API.Client.createApiClient({
          apiEndpoint: gqlApiEndpoint.value,
          middleware: [accountMiddleware(accountId, requireAccount), appIdMiddleware(gqlApiAppId, gqlApiApp), authMiddleware, langMiddleware(localeRef)],
        });
      }
    },
  );

  // ADR-009: the internal surface shares the /graphql path today, so it falls back
  // to the regular GraphQL endpoint. A later runtime extraction changes only this.
  const internalEndpoint = computed(() => internalApiEndpoint.value || gqlApiEndpoint.value);

  const internalGqlApiClient = computed(
    (): ApolloClient<NormalizedCacheObject> | undefined => {
      if (!internalEndpoint.value) {
        throw new Error('neither internalApiEndpoint nor gqlApiEndpoint is set');
      }

      // accountMiddleware is load-bearing: InternalGraphqlController#build_vp_context
      // resolves the account from x-vp-account-id.
      //
      // It is given `ACCOUNT_OPTIONAL` rather than the store's `requireAccount`: this schema is a
      // VenuePilot-staff surface and most of it is cross-account by construction — the platform
      // account list, the fee structures, the account pickers. Refusing those for want of an
      // account would break every staff screen for an operator who belongs to no tenant account,
      // which is exactly who administers other people's. The operations that DO act on a single
      // account name it themselves with `forAccount`, which wins over the store either way, so the
      // account still reaches the request that needs it.
      //
      // The gate itself reads the signed session cookie only, so authMiddleware just
      // rides along for parity and the backend ignores it. Drop it from this chain if
      // internalApiEndpoint is ever pointed at a different host, so the admin bearer
      // token is not shipped somewhere with no business seeing it.
      const middleware = [accountMiddleware(accountId, ACCOUNT_OPTIONAL), internalApiMiddleware, authMiddleware, langMiddleware(localeRef)];

      // The gate reads the signed session cookie, so the request has to carry it. Apollo's default
      // is `same-origin`, which only happens to work because `internalApiEndpoint` is unset today
      // and the internal client falls back to the shared endpoint; the moment it names another
      // host the cookie is dropped and every staff screen 401s for good.
      //
      // Asked for only when a dedicated endpoint is configured, which is exactly the case that
      // needs it. On the fallback path `same-origin` already sends the cookie, and `include`
      // there would be a live risk rather than a no-op: `gqlApiEndpoint` can itself be an
      // absolute cross-origin URL in the standalone build, where a credentialed request against
      // `Access-Control-Allow-Origin: *` is discarded by the browser -- turning today's cleanly
      // classified 401 into an unclassified network error, plus a Sentry exception per read.
      //
      // The day the dedicated endpoint lands has a server-side half:
      // `Access-Control-Allow-Credentials: true` with a concrete origin, never a wildcard -- and
      // the host has to stay same-site, because the backend writes this cookie `SameSite=Strict`,
      // which drops it across registrable domains whatever `credentials` says.
      const credentials = internalApiEndpoint.value ? 'include' : undefined;

      if (typeof window !== 'undefined') {
        // We're in browser, we can cache the client
        if (!internalGqlApiClientInstance) {
          internalGqlApiClientInstance = API.Client.createApiClient({
            apiEndpoint: internalEndpoint.value,
            credentials,
            middleware,
          });
        }
        return internalGqlApiClientInstance;
      } else {
        // In SSR, always create a new instance so it doesn't leak between requests
        return API.Client.createApiClient({
          apiEndpoint: internalEndpoint.value,
          credentials,
          middleware,
        });
      }
    },
  );

  // `requireAccount` is not a client-construction parameter, so it is not part of
  // `InitClientParams`; it is widened here because `init` is where a consumer declares how it
  // intends to use the store.
  const init = (options: API.Client.InitClientParams & { requireAccount?: boolean }) => {
    apiBase.value = options.apiBase;
    gqlApiEndpoint.value = options.apiEndpoint;
    appId.value = options.appId;
    apiApp.value = options.apiApp;
    gqlApiApp.value = options.gqlApiApp;
    gqlApiAppId.value = options.gqlApiAppId;
    internalApiEndpoint.value = options.internalApiEndpoint;
    prependMiddleware.value = options.middleware;
    requireAccount.value = options.requireAccount ?? false;
  };

  const setAuthToken = (token: string) => {
    authToken.value = token;
    if (typeof window !== 'undefined') {
      localStorage.setItem(AUTH_TOKEN_KEY, token);
    }
  };

  const setLocale = (locale: string | undefined) => {
    localeRef.value = locale;
  };

  return {
    accountId,
    requireAccount,
    apiClient,
    gqlApiClient,
    internalGqlApiClient,
    apiBase,
    gqlApiEndpoint,
    internalApiEndpoint,
    authToken,
    localeRef,
    setLocale,
    init,
    setAuthToken,
  };
});
